import { Badge } from "design-system/atoms/Badge"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { useKnowledgeTableSort } from "modules/backoffice/lib/knowledge/useKnowledgeTableSort"
import { useTranslation } from "react-i18next"
import { BackofficeKnowledgeItemsResponseDataSchema } from "schema"
import { z } from "zod"
import { BackofficeKnowledgeSection } from "./BackofficeKnowledgeSection"

type Items = z.infer<typeof BackofficeKnowledgeItemsResponseDataSchema>
type Item = Items[number]

type Props = {
  data: Items | undefined
  isPending: boolean
}

const COLUMN_COUNT = 6
const NOISY_MIN_RETRIEVED_COUNT = 10
const NOISY_MAX_PRECISION_RATE = 20

export function BackofficeKnowledgeItemsTable({ data, isPending }: Props) {
  const { t } = useTranslation()
  const { sortedData, sortable } = useKnowledgeTableSort(data, {
    title: (row) => row.title ?? row.knowledgeBaseItemId,
    kind: (row) => row.kind,
    retrievedCount: (row) => row.retrievedCount,
    usedCount: (row) => row.usedCount,
    precisionRate: (row) => row.precisionRate,
    answeredQuestionCount: (row) => row.answeredQuestionCount,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={Array.from({ length: COLUMN_COUNT }, () => 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate colSpan={COLUMN_COUNT} message={t("backoffice.knowledge.items.empty")} />
      )
    }

    return sortedData.map((item) => {
      const isDead = item.retrievedCount === 0
      const isNoisy = isNoisyItem(item)

      return (
        <TableRow
          key={item.knowledgeBaseItemId}
          className={cn(isDead && "text-muted-foreground", isNoisy && "bg-warning/5")}
        >
          <TableCell>
            <div className="flex items-center gap-2">
              <span className="max-w-md truncate" title={item.title ?? item.knowledgeBaseItemId}>
                {item.title ?? item.knowledgeBaseItemId}
              </span>
              {isDead && (
                <Badge variant="cached">{t("backoffice.knowledge.items.neverRetrieved")}</Badge>
              )}
              {isNoisy && <Badge variant="warning">{t("backoffice.knowledge.items.noisy")}</Badge>}
            </div>
          </TableCell>
          <TableCell>
            <Badge variant="secondary">{item.kind}</Badge>
          </TableCell>
          <TableCell align="right">{item.retrievedCount}</TableCell>
          <TableCell align="right">{item.usedCount}</TableCell>
          <TableCell align="right">{formatKnowledgeRate(item.precisionRate)}</TableCell>
          <TableCell align="right">{item.answeredQuestionCount}</TableCell>
        </TableRow>
      )
    })
  }

  return (
    <BackofficeKnowledgeSection
      title={t("backoffice.knowledge.items.title")}
      description={t("backoffice.knowledge.items.description")}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead sortable={sortable("title")}>
              {t("backoffice.knowledge.items.item")}
            </TableHead>
            <TableHead sortable={sortable("kind")}>
              {t("backoffice.knowledge.items.kind")}
            </TableHead>
            <TableHead align="right" sortable={sortable("retrievedCount")}>
              {t("backoffice.knowledge.items.retrievedCount")}
            </TableHead>
            <TableHead align="right" sortable={sortable("usedCount")}>
              {t("backoffice.knowledge.items.usedCount")}
            </TableHead>
            <TableHead align="right" sortable={sortable("precisionRate")}>
              {t("backoffice.knowledge.items.precisionRate")}
            </TableHead>
            <TableHead align="right" sortable={sortable("answeredQuestionCount")}>
              {t("backoffice.knowledge.items.answeredQuestionCount")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </BackofficeKnowledgeSection>
  )
}

function isNoisyItem(item: Item) {
  return (
    item.retrievedCount >= NOISY_MIN_RETRIEVED_COUNT &&
    item.precisionRate !== null &&
    item.precisionRate <= NOISY_MAX_PRECISION_RATE
  )
}
